// @flow
import * as React from 'react';
import { Observation, ModerationState } from 'models';
import { ProductSection, ContentSection } from '../../';
import { ObservationButton } from '../../Common';
import CardAlert from '../../Common/CardAlert';
import {
  powerModerationCard as cardCls,
  powerModerationCardMain as mainCls,
  powerModerationCardOverlay,
} from './PowerReviewsModerationCard.scss';
import SectionSeparator from '../SectionSeparator';
import ActionAsideUnmoderated from './ActionAsideUnmoderated';
import ActionAsideModerated from './ActionAsideModerated';
import ActionAsideSecondOpinion from './ActionAsideSecondOpinion';
import SecondOpinionCard from './SecondOpinionCard';

const { isPowerReviewsModerated, getActionableObservations } = ModerationState;

const renderObservationButtons = (
  observations: Array<Observation>,
  onClick: string => void
): {
  totalSelectedCount: number,
  standardObservationButtons: Array<ObservationButton>,
  customObservationButtons: Array<ObservationButton>,
} => {
  let selectedCount = 0;
  const standardObservations = observations.filter(
    o => o.custom_observation === false
  );
  const customObservations = observations.filter(
    o => o.custom_observation === true
  );

  const standardObservationButtonsObject = createObservationButtons(
    standardObservations,
    onClick,
    selectedCount
  );
  const standardObservationButtons = standardObservationButtonsObject.buttons;

  const customObservationButtonsObject = createObservationButtons(
    customObservations,
    onClick,
    selectedCount
  );
  const customObservationButtons = customObservationButtonsObject.buttons;

  const totalSelectedCount =
    standardObservationButtonsObject.selectedCount +
    customObservationButtonsObject.selectedCount;

  return {
    totalSelectedCount,
    standardObservationButtons,
    customObservationButtons,
  };
};

function createObservationButtons(
  observations: Array<Observation>,
  onClick: string => void,
  selectedCount: number
) {
  const observationButtons = observations.map((ob: Observation) => {
    const { name, observation_type_code } = ob;
    const existing = Observation.isSelected(ob);

    if (existing) {
      selectedCount += 1;
    }

    return (
      <ObservationButton
        key={observation_type_code}
        name={name}
        observation_type_code={observation_type_code}
        onClick={onClick}
        existing={existing}
      />
    );
  });

  return {
    selectedCount: selectedCount,
    buttons: observationButtons,
  };
}

type PowerReviewsModerationCardState = {
  showSecondOpinionCard: boolean,
  escalationReason: string,
};

type Props = {
  product: Product,
  ugc: Object,
  moderationState: ModerationState,
  onChange: ModerationState => void,
  onSubmit: (ModerationState, Id) => void,
  status: ?RequestStatus,
};

export default class PowerReviewsModerationCard extends React.PureComponent<
  Props,
  PowerReviewsModerationCardState
> {
  state = {
    showSecondOpinionCard: false,
    escalationReason: '',
  };

  productSection: ?ProductSection;
  contentSection: ?ContentSection;

  handleObservationChange = (observation_type_code: string) => {
    const { moderationState: currentState, onChange } = this.props;
    const nextState = ModerationState.toggleObservation(
      currentState,
      ModerationState.PowerReviewsModerationContext,
      observation_type_code
    );

    onChange && onChange(nextState);
  };

  handleEscalationReasonChange = (escalationReason: string) => {
    this.setState({ escalationReason });
  };

  handleSubmit = () => {
    const { moderationState, onSubmit } = this.props;
    const { escalationReason, showSecondOpinionCard } = this.state;
    const stateToSubmit =
      showSecondOpinionCard === true
        ? ModerationState.applySecondOpinion(moderationState, escalationReason)
        : ModerationState.applyPowerModeration(moderationState);

    this.setState({ showSecondOpinionCard: false }, () => {
      onSubmit(stateToSubmit, ModerationState.PowerReviewsModerationContext);
    });
  };

  toggleSecondOpinionCard = () =>
    this.setState(prevState => {
      return { showSecondOpinionCard: !prevState.showSecondOpinionCard };
    });

  componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps.moderationState.getId() !== this.props.moderationState.getId()
    ) {
      this.setState({
        showSecondOpinionCard: false,
        escalationReason: '',
      });
    }
  }

  renderActionAside(props: Props) {
    const { moderationState, status, product, ugc } = props;
    const isPwrModerated = isPowerReviewsModerated(moderationState);
    const secondOpinionRequired = ModerationState.needsSecondOpinion(
      moderationState
    );
    const observations = getActionableObservations(
      moderationState,
      ModerationState.PowerReviewsModerationContext
    );
    const merchantName = product.merchant.group_name;

    const {
      standardObservationButtons,
      customObservationButtons,
      totalSelectedCount: selectedCount,
    } = renderObservationButtons(observations, this.handleObservationChange);
    let actionAside;
    const pending = status === 'pending';

    if (secondOpinionRequired) {
      actionAside = (
        <ActionAsideSecondOpinion
          pending={pending}
          onSubmit={this.handleSubmit}
          moderationState={moderationState}
          standardObservationButtons={standardObservationButtons}
          customObservationButtons={customObservationButtons}
          merchantName={merchantName}
        />
      );
    } else if (isPwrModerated) {
      actionAside = (
        <ActionAsideModerated
          pending={pending}
          onSubmit={this.handleSubmit}
          moderationState={moderationState}
          standardObservationButtons={standardObservationButtons}
          customObservationButtons={customObservationButtons}
          merchantName={merchantName}
        />
      );
    } else {
      actionAside = (
        <ActionAsideUnmoderated
          pending={pending}
          selectedCount={selectedCount}
          ugcType={ugc.ugctype}
          onSubmit={this.handleSubmit}
          onSecondOpinionClick={this.toggleSecondOpinionCard}
          standardObservationButtons={standardObservationButtons}
          customObservationButtons={customObservationButtons}
          merchantName={merchantName}
        />
      );
    }

    return actionAside;
  }

  toggleSubjectDetails(cb: *) {
    if (this.productSection) {
      this.productSection.toggleDetails(true);
      typeof cb === 'function' && cb();
    }
  }

  toggleMeta(cb: *) {
    if (this.contentSection) {
      this.contentSection.toggleMeta(true);
      typeof cb === 'function' && cb();
    }
  }

  showSecondOpinionCard(cb: *) {
    if (this.state.showSecondOpinionCard === false) {
      typeof cb === 'function' && cb();
      this.setState({
        showSecondOpinionCard: true,
      });
    }
  }

  render() {
    const { moderationState, product, ugc, status } = this.props;
    const isUpdatedUgc = ugc.old_ugc_id ? true : false;
    const observations = getActionableObservations(
      moderationState,
      ModerationState.PowerReviewsModerationContext
    );

    const ugcId = moderationState.getUGCId();
    const pending = status === 'pending';

    // TODO: we shouldn't need this if we trust action aside
    if (this.state.showSecondOpinionCard) {
      return (
        <div className={cardCls}>
          <SecondOpinionCard
            key={`em-${ugcId}`}
            observations={observations}
            onObserve={this.handleObservationChange}
            onSubmit={this.handleSubmit}
            onClose={this.toggleSecondOpinionCard}
            reason={this.state.escalationReason}
            onReasonChange={this.handleEscalationReasonChange}
          />
        </div>
      );
    }

    return (
      <div className={cardCls}>
        {pending === true && <div className={powerModerationCardOverlay} />}
        <div className={mainCls}>
          <ProductSection
            model={product}
            key={product.subject_id}
            merchantName
            brandName
            ref={c => (this.productSection = c)}
          />
          <div className="mb4">
            <SectionSeparator />
          </div>

          {isUpdatedUgc && (
            <CardAlert>
              This review is an update to a previous review.
            </CardAlert>
          )}

          <ContentSection
            key={`cc-${ugcId}`}
            model={ugc}
            program="pwr"
            ref={c => (this.contentSection = c)}
          />
        </div>
        {this.renderActionAside(this.props)}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ModerationCard/PowerReviewsModerationCard/PowerReviewsModerationCard.js